import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';

// Create the context
const FranchiseSelectorContext = createContext({
  selectedFranchiseId: null,
  setSelectedFranchiseId: () => {},
  availableFranchises: [],
  setAvailableFranchises: () => {},
  isAdmin: false
});

// Custom hook to use franchise selector context
export const useFranchiseSelector = () => {
  const context = useContext(FranchiseSelectorContext);
  if (!context) {
    throw new Error('useFranchiseSelector must be used within a FranchiseSelectorProvider');
  }
  return context;
};

// Franchise selector provider component
export const FranchiseSelectorProvider = ({ children }) => {
  const { userProfile, isAdmin } = useAuth();
  const [selectedFranchiseId, setSelectedFranchiseId] = useState(null);
  const [availableFranchises, setAvailableFranchises] = useState([]);

  // Load selected franchise from localStorage on mount
  useEffect(() => {
    if (isAdmin()) {
      const saved = localStorage.getItem('selectedFranchiseId');
      if (saved) {
        setSelectedFranchiseId(saved);
      }
    } else if (userProfile?.franchiseId) {
      // Non-admins are locked to their franchise
      setSelectedFranchiseId(userProfile.franchiseId);
    }
  }, [isAdmin, userProfile]);

  // Save selected franchise to localStorage when it changes
  useEffect(() => {
    if (isAdmin() && selectedFranchiseId) {
      localStorage.setItem('selectedFranchiseId', selectedFranchiseId);
    }
  }, [selectedFranchiseId, isAdmin]);

  // Update selected franchise function
  const updateSelectedFranchise = (franchiseId) => {
    if (isAdmin()) {
      setSelectedFranchiseId(franchiseId);
    } else {
      console.warn('Only administrators can change selected franchise');
    }
  };

  const value = {
    selectedFranchiseId,
    setSelectedFranchiseId: updateSelectedFranchise,
    availableFranchises,
    setAvailableFranchises,
    isAdmin: isAdmin()
  };

  return (
    <FranchiseSelectorContext.Provider value={value}>
      {children}
    </FranchiseSelectorContext.Provider>
  );
};

export default FranchiseSelectorContext; 